import {
  AccessoriesDueList,
  AccessoriesDueListQuery,
  DeviceAccessorySlot,
  DeviceBrandsList,
  DevicesList,
  DevicesListQuery,
  CreateDeviceDto,
  DeviceListItem,
  DeviceHistoryItemDto,
} from '../types'
import { baseApi } from './baseApi'

export const deviceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDeviceBrands: builder.query<DeviceBrandsList, { userType: string }>({
      query: ({ userType }) => ({
        url: `/${userType === 'DME' ? 'dme' : 'lab'}/device-brands/list`,
      }),
      providesTags: ['DeviceBrands'],
    }),
    getLabDevices: builder.query<DevicesList, DevicesListQuery>({
      query: (query) => ({ url: `/lab/devices/list`, params: query }),
      providesTags: ['Devices'],
    }),
    getLabDevice: builder.query<DeviceListItem, number>({
      query: (id) => ({ url: `/lab/devices/${id}` }),
      providesTags: ['Devices'],
    }),
    getLabDeviceHistory: builder.query<DeviceHistoryItemDto[], number>({
      query: (id) => ({ url: `/lab/devices/${id}/history` }),
      providesTags: ['Devices'],
    }),
    createLabDevice: builder.mutation<DeviceListItem, CreateDeviceDto>({
      query: (body) => ({ url: `/lab/devices`, method: 'POST', body }),
      invalidatesTags: ['Devices'],
    }),
    createDmeDevice: builder.mutation<void, CreateDeviceDto>({
      query: (body) => ({ url: `/dme/devices`, method: 'POST', body }),
      invalidatesTags: ['Devices'],
    }),
    deleteLabDevice: builder.mutation<void, number>({
      query: (id) => ({ url: `/lab/devices/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Devices'],
    }),
    updateLabDevice: builder.mutation<void, { id: number } & CreateDeviceDto>({
      query: (body) => ({ url: `/lab/devices/${body.id}`, method: 'PATCH', body }),
      invalidatesTags: ['Devices'],
    }),
    updateDmeDevice: builder.mutation<void, { id: number } & CreateDeviceDto>({
      query: (body) => ({ url: `/dme/devices/${body.id}`, method: 'PATCH', body }),
      invalidatesTags: ['Devices'],
    }),
    getDmeDevices: builder.query<DevicesList, DevicesListQuery>({
      query: (params) => ({ url: `/dme/devices/list`, params }),
      providesTags: ['Devices'],
    }),
    getDmeDeviceBrands: builder.query<DeviceBrandsList, void>({
      query: () => ({ url: `/dme/device-brands/list` }),
      providesTags: ['DeviceBrands'],
    }),
    // TES-3191: the Accessory Replacement bucket
    getDevicesWithAccessoriesDue: builder.query<AccessoriesDueList, AccessoriesDueListQuery>({
      query: (params) => ({ url: `/lab/devices/accessories-due`, params }),
      providesTags: ['Devices'],
    }),
    // puts an accessory on its slot, replacing the one there; it counts from zero
    installDeviceAccessory: builder.mutation<
      void,
      { id: number; type: DeviceAccessorySlot['type']; slot: number; serial: string | null }
    >({
      query: ({ id, ...body }) => ({ url: `/lab/devices/${id}/accessories`, method: 'POST', body }),
      invalidatesTags: ['Devices', 'OrderCounts'],
    }),
    // puts a mistyped serial right; the accessory keeps its count
    correctDeviceAccessorySerial: builder.mutation<void, { id: number; accessoryId: string; serial: string | null }>({
      query: ({ id, accessoryId, serial }) => ({
        url: `/lab/devices/${id}/accessories/${accessoryId}`,
        method: 'PATCH',
        body: { serial },
      }),
      invalidatesTags: ['Devices'],
    }),
    overrideDeviceFirstCheckOut: builder.mutation<void, { id: number; firstCheckOutDate: string }>({
      query: ({ id, firstCheckOutDate }) => ({
        url: `/lab/devices/${id}/first-check-out`,
        method: 'PUT',
        body: { firstCheckOutDate },
      }),
      invalidatesTags: ['Devices'],
    }),
  }),
})

export const {
  useGetDeviceBrandsQuery,
  useGetLabDevicesQuery,
  useLazyGetLabDevicesQuery,
  useGetLabDeviceQuery,
  useGetLabDeviceHistoryQuery,
  useCreateLabDeviceMutation,
  useCreateDmeDeviceMutation,
  useUpdateLabDeviceMutation,
  useUpdateDmeDeviceMutation,
  useGetDmeDevicesQuery,
  useDeleteLabDeviceMutation,
  useLazyGetDmeDevicesQuery,
  useGetDmeDeviceBrandsQuery,
  useGetDevicesWithAccessoriesDueQuery,
  useInstallDeviceAccessoryMutation,
  useCorrectDeviceAccessorySerialMutation,
  useOverrideDeviceFirstCheckOutMutation,
} = deviceApi
